import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./AdminAllUserScores.css";

const AdminAllUserScores = () => {
  const [allUserData, setAllUserData] = useState([]);
  const [error, setError] = useState(null);
  const [filteredData, setFilteredData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortKey, setSortKey] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");
  const [filters, setFilters] = useState({
    role: "",
    quizType: "",
    startDate: "",
    endDate: "",
  });
  const [roleOptions, setRoleOptions] = useState([]);
  const navigate = useNavigate();

  const [userData, setUserData] = useState(null);
  const [minDate, setMinDate] = useState("");
  const [maxDate, setMaxDate] = useState("");

  // Fetch user data from the backend
  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_FLASK_API_URL}/dashboard`, {
      method: "GET",
      credentials: "include",
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.error) {
          console.error(data.error);
          setError(data.error);
        } else {
          setUserData(data);
        }
      })
      .catch((error) => {
        console.error("Error fetching user data:", error);
        setError("Error fetching user data");
      });
  }, []);

  useEffect(() => {
    if (!allUserData || allUserData.length === 0) {
      setMinDate("");
      setMaxDate("");
      return;
    }
    const dates = allUserData
      .map((item) => {
        if (item.date) {
          const d = new Date(item.date);
          return isNaN(d.getTime()) ? null : d;
        }
        return null;
      })
      .filter(Boolean);

    if (dates.length > 0) {
      const minDateObj = new Date(Math.min(...dates));
      const maxDateObj = new Date(Math.max(...dates));
      // Format as YYYY-MM-DD
      const formatLocalDate = (date) =>
        date.getFullYear() +
        "-" +
        String(date.getMonth() + 1).padStart(2, "0") +
        "-" +
        String(date.getDate()).padStart(2, "0");
      setMinDate(formatLocalDate(minDateObj));
      setMaxDate(formatLocalDate(maxDateObj));
    } else {
      setMinDate("");
      setMaxDate("");
    }
  }, [allUserData]);

  const empathyScoreHighThreshold = 0.6;
  const empathyScoreLowThreshold = 0.3;

  const getScoreColor = (value) => {
    if (value > empathyScoreHighThreshold * 100) return "green";
    if (value < empathyScoreLowThreshold * 100) return "red";
    return "orange";
  };

  const getPercent = (raw, max) => {
    if (!max) return 0;
    return ((raw / max) * 100).toFixed(1);
  };

  const getTotalNormalised = (score, maxScore) => {
    const cats = ["Cognitive", "Affective", "Empathic Response"];
    let totalRaw = 0;
    let totalMax = 0;
    cats.forEach((cat) => {
      totalRaw += Number(score?.[cat] ?? 0);
      totalMax += Number(maxScore?.[cat] ?? 0);
    });
    if (!totalMax) return 0;
    return ((totalRaw / totalMax) * 100).toFixed(1);
  };

  useEffect(() => {
    if (!userData) return;

    fetch(`${process.env.REACT_APP_BACKEND_FLASK_API_URL}/admin/data`, {
      method: "GET",
      credentials: "include",
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.error) {
          setError(data.error);
        } else {
          // Flatten all scores for all users
          const flattened = [];
          data.forEach((user) => {
            (user.scores || []).forEach((scoreEntry) => {
              const isoDate = scoreEntry?.date?.$date || scoreEntry?.date;
              const formattedDate = isoDate
                ? new Date(isoDate).toLocaleDateString("en-GB")
                : "N/A";
              flattened.push({
                username: user.username,
                role: user.role_title,
                score: scoreEntry.score,
                maxScore: scoreEntry.maxScore,
                date: isoDate,
                displayDate: formattedDate,
                quiz_type: scoreEntry.quiz,
              });
            });
          });
          setAllUserData(flattened);
          setFilteredData(flattened);

          const uniqueRoles = [...new Set(flattened.map((item) => item.role))];
          setRoleOptions(uniqueRoles);
        }
      })
      .catch(() => {
        setError("Error fetching data");
      });
  }, [userData]);

  useEffect(() => {
    let data = [...allUserData];

    if (searchTerm) {
      data = data.filter((item) =>
        item.username.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (filters.role) data = data.filter((item) => item.role === filters.role);
    if (filters.quizType)
      data = data.filter((item) => item.quiz_type === filters.quizType);
    if (filters.startDate)
      data = data.filter(
        (item) =>
          new Date(item.date) >= new Date(filters.startDate + "T00:00:00")
      );
    if (filters.endDate)
      data = data.filter(
        (item) =>
          new Date(item.date) <= new Date(filters.endDate + "T23:59:59.999")
      );

    if (sortKey) {
      data.sort((a, b) => {
        let aValue, bValue;
        switch (sortKey) {
          case "cognitive":
            aValue = getPercent(
              a.score?.["Cognitive"] ?? 0,
              a.maxScore?.["Cognitive"] ?? 0
            );
            bValue = getPercent(
              b.score?.["Cognitive"] ?? 0,
              b.maxScore?.["Cognitive"] ?? 0
            );
            break;
          case "affective":
            aValue = getPercent(
              a.score?.["Affective"] ?? 0,
              a.maxScore?.["Affective"] ?? 0
            );
            bValue = getPercent(
              b.score?.["Affective"] ?? 0,
              b.maxScore?.["Affective"] ?? 0
            );
            break;
          case "empathic":
            aValue = getPercent(
              a.score?.["Empathic Response"] ?? 0,
              a.maxScore?.["Empathic Response"] ?? 0
            );
            bValue = getPercent(
              b.score?.["Empathic Response"] ?? 0,
              b.maxScore?.["Empathic Response"] ?? 0
            );
            break;
          case "total":
            aValue = getTotalNormalised(a.score, a.maxScore);
            bValue = getTotalNormalised(b.score, b.maxScore);
            break;
          default:
            aValue = a[sortKey];
            bValue = b[sortKey];
        }
        // Convert to number for percent and total
        if (["cognitive", "affective", "empathic", "total"].includes(sortKey)) {
          aValue = Number(aValue);
          bValue = Number(bValue);
        }
        if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
        if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
        return 0;
      });
    }

    setFilteredData(data);
  }, [searchTerm, filters, sortKey, sortDirection, allUserData]);

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortKey(key);
      setSortDirection("asc");
    }
  };

  const handleLogout = () => {
    fetch(`${process.env.REACT_APP_BACKEND_FLASK_API_URL}/logout`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
    })
      .then((response) => response.json())
      .then((data) => {
        console.log(data.message);
        navigate(`/`);
      })
      .catch((error) => console.error("Error logging out:", error));
  };

  const handleBackToDashboard = () => navigate("/dashboard");

  return (
    <div className="admin-scores">
      <div className="dash-header">
        <button onClick={handleBackToDashboard}>Dashboard</button>
        <button onClick={handleLogout}>Log Out</button>
      </div>

      <h2>All User Scores</h2>

      <div className="filters">
        <div className="filter-group">
          <label>🔍 Search by Username</label>
          <input
            type="text"
            placeholder="e.g. johndoe"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="filter-group">
          <label>🎓 Filter by Role</label>
          <select
            value={filters.role}
            onChange={(e) => setFilters({ ...filters, role: e.target.value })}
          >
            <option value="">All Roles</option>
            {roleOptions.map((role) => (
              <option key={role} value={role}>
                {role}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label>📝 Filter by Quiz Type</label>
          <select
            value={filters.quizType}
            onChange={(e) =>
              setFilters({ ...filters, quizType: e.target.value })
            }
          >
            <option value="">All Quiz Types</option>
            <option value="user">User</option>
            <option value="practitioner">Practitioner</option>
          </select>
        </div>

        <div className="filter-group">
          <label>📅 Start Date</label>
          <input
            type="date"
            value={filters.startDate}
            onChange={(e) =>
              setFilters({ ...filters, startDate: e.target.value })
            }
            min={minDate || undefined}
            max={
              filters.endDate
                ? filters.endDate < maxDate
                  ? filters.endDate
                  : maxDate
                : maxDate || undefined
            }
          />
        </div>

        <div className="filter-group">
          <label>📅 End Date</label>
          <input
            type="date"
            value={filters.endDate}
            onChange={(e) =>
              setFilters({ ...filters, endDate: e.target.value })
            }
            min={
              filters.startDate
                ? filters.startDate > minDate
                  ? filters.startDate
                  : minDate
                : minDate || undefined
            }
            max={maxDate || undefined}
          />
        </div>
      </div>

      <div style={{ marginBottom: "8px", fontStyle: "italic", color: "#555" }}>
        Tip: Click a column header to sort by that column.
      </div>

      <table>
        <thead>
          <tr>
            <th onClick={() => handleSort("username")}>
              Username{" "}
              {sortKey === "username" && (sortDirection === "asc" ? "▲" : "▼")}
            </th>
            <th onClick={() => handleSort("role")}>
              Role {sortKey === "role" && (sortDirection === "asc" ? "▲" : "▼")}
            </th>
            <th onClick={() => handleSort("cognitive")}>
              Cognitive{" "}
              {sortKey === "cognitive" && (sortDirection === "asc" ? "▲" : "▼")}
            </th>
            <th onClick={() => handleSort("affective")}>
              Affective{" "}
              {sortKey === "affective" && (sortDirection === "asc" ? "▲" : "▼")}
            </th>
            <th onClick={() => handleSort("empathic")}>
              Empathic Response{" "}
              {sortKey === "empathic" && (sortDirection === "asc" ? "▲" : "▼")}
            </th>
            <th onClick={() => handleSort("total")}>
              Total Normalised Score{" "}
              {sortKey === "total" && (sortDirection === "asc" ? "▲" : "▼")}
            </th>
            <th onClick={() => handleSort("date")}>
              Date Taken{" "}
              {sortKey === "date" && (sortDirection === "asc" ? "▲" : "▼")}
            </th>
            <th onClick={() => handleSort("quiz_type")}>
              Quiz Type{" "}
              {sortKey === "quiz_type" && (sortDirection === "asc" ? "▲" : "▼")}
            </th>
          </tr>
        </thead>
        <tbody>
          {filteredData.length > 0 ? (
            filteredData.map((user, index) => {
              const cogRaw = user.score?.["Cognitive"] ?? 0;
              const cogMax = user.maxScore?.["Cognitive"] ?? 0;
              const cogPercent = getPercent(cogRaw, cogMax);

              const affRaw = user.score?.["Affective"] ?? 0;
              const affMax = user.maxScore?.["Affective"] ?? 0;
              const affPercent = getPercent(affRaw, affMax);

              const empRaw = user.score?.["Empathic Response"] ?? 0;
              const empMax = user.maxScore?.["Empathic Response"] ?? 0;
              const empPercent = getPercent(empRaw, empMax);

              const totalPercent = getTotalNormalised(
                user.score,
                user.maxScore
              );

              return (
                <tr key={index}>
                  <td>{user.username}</td>
                  <td>{user.role}</td>
                  <td>
                    {cogRaw} / {cogMax} (
                    <span
                      style={{
                        color: getScoreColor(cogPercent),
                        fontWeight: "bold",
                      }}
                    >
                      {cogPercent}%
                    </span>
                    )
                  </td>
                  <td>
                    {affRaw} / {affMax} (
                    <span
                      style={{
                        color: getScoreColor(affPercent),
                        fontWeight: "bold",
                      }}
                    >
                      {affPercent}%
                    </span>
                    )
                  </td>
                  <td>
                    {empRaw} / {empMax} (
                    <span
                      style={{
                        color: getScoreColor(empPercent),
                        fontWeight: "bold",
                      }}
                    >
                      {empPercent}%
                    </span>
                    )
                  </td>
                  <td>
                    <span
                      style={{
                        color: getScoreColor(totalPercent),
                        fontWeight: "bold",
                      }}
                    >
                      {totalPercent}
                    </span>
                  </td>
                  <td>{user.displayDate}</td>
                  <td>{user.quiz_type}</td>
                </tr>
              );
            })
          ) : (
            <tr>
              <td colSpan="8">No scores available</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default AdminAllUserScores;
